// Snowden User Intelligence — reviewed first-party theme telemetry v0.2
// This module observes only explicitly allowlisted data attributes and the
// reviewed capture points listed in REVIEWED_CAPTURES below. It never reads
// clicked text or arbitrary DOM content, and reads form fields only from a
// data-snowden-identity="true" form or a reviewed capture point.
(function () {
  "use strict";
  const ENDPOINT = "https://snowden-web-event-collector-w5zi6rn6mq-uc.a.run.app/v1/events";
  const VISITOR_KEY = "snowden_visitor_id";
  const SESSION_KEY = "snowden_session_id";
  // Klaviyo email links carry ?skid={{ person.KlaviyoID }} (Klaviyo UTM settings).
  const KLAVIYO_LINK_PARAM = "skid";
  const allowedHosts = new Set(["svenandson.com", "www.svenandson.com"]);
  const allowedPurposes = new Set(["product_consultation", "financing", "checkout", "contact", "newsletter", "warranty", "other"]);
  const allowedRegions = new Set(["header", "navigation", "hero", "collection", "product_gallery", "product_details", "configuration", "comparison", "financing", "reviews", "faq", "cart_drawer", "checkout", "footer", "other"]);
  if (!allowedHosts.has(window.location.hostname)) return;

  // Reviewed capture points on svenandson.com that predate data-snowden markers.
  // Each entry names exactly which element is read; nothing else on the page is.
  const REVIEWED_CAPTURES = [
    // Bed / mattress / back-pain quizzes: "Get your results via email".
    { id: "sven_quiz_results", purpose: "product_consultation", container: "[data-email-capture]",
      email: "[data-email-input]", submit: "[data-email-submit]", emailMarketing: false },
  ];
  // Shopify-native contact and newsletter forms (sections/contact-form, sven-contact-form,
  // sven-help-form, newsletter, sven-newsletter). Newsletter forms carry contact[tags]=newsletter.
  const SHOPIFY_FORM_SELECTOR = 'form[action*="/contact"]:not([data-snowden-form-id])';
  // Financing entry points. The provider label comes from which selector matched, never from text.
  const FINANCING_PROVIDERS = [
    ["affirm", '[data-snowden-financing="affirm"], a[href*="affirm.com"], .affirm-as-low-as, [id*="affirm"] button'],
    ["synchrony", '[data-snowden-financing="synchrony"], a[href*="mysynchrony.com"], a[href*="syf.com"]'],
    ["flex", '[data-snowden-financing="flex"], a[href*="withflex.com"], [id*="flex_pay"] button'],
    ["klarna", '[data-snowden-financing="klarna"], klarna-placement, a[href*="klarna.com"]'],
  ];

  let started = false;
  let engagedMs = 0;
  let visibleStartedAt = document.visibilityState === "visible" ? Date.now() : null;
  let maxScroll = 0;
  let clarityCustomIdPromise;
  let clarityIdentified = false;

  // Remove the Klaviyo profile ID from the address bar immediately, whatever the
  // consent state, so later page views, shares and bookmarks never carry it.
  const linkedKlaviyoProfileId = (function takeKlaviyoLinkParam() {
    try {
      const url = new URL(window.location.href);
      const value = url.searchParams.get(KLAVIYO_LINK_PARAM);
      if (!value) return null;
      url.searchParams.delete(KLAVIYO_LINK_PARAM);
      window.history.replaceState(window.history.state, "", url.pathname + (url.search ? url.search : "") + url.hash);
      return /^[0-9A-Za-z]{20,32}$/.test(value) ? value : null;
    } catch (_) { return null; }
  }());

  function uuid() {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function cookie(name) {
    const prefix = `${name}=`;
    const part = document.cookie.split("; ").find((value) => value.startsWith(prefix));
    return part ? part.slice(prefix.length) : null;
  }

  function googleClientId() {
    const value = cookie("_ga");
    if (!value) return undefined;
    const parts = value.split(".");
    const candidate = parts.length >= 2 ? parts.slice(-2).join(".") : "";
    return /^\d{1,20}\.\d{1,20}$/.test(candidate) ? candidate : undefined;
  }

  function clarityCustomId(visitorId) {
    if (!clarityCustomIdPromise) {
      clarityCustomIdPromise = crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(`snowden:clarity:${visitorId}`),
      ).then((bytes) => `snowden_${Array.from(new Uint8Array(bytes),
        (value) => value.toString(16).padStart(2, "0")).join("")}`).catch(() => undefined);
    }
    return clarityCustomIdPromise;
  }

  async function sourceIds(visitorId) {
    const clarityId = await clarityCustomId(visitorId);
    if (clarityId && !clarityIdentified) {
      // Clarity loads late (tag manager, consent). Install Microsoft's own queue stub
      // when it isn't there yet so the identify call waits for Clarity instead of
      // being skipped; Clarity replays window.clarity.q when it starts.
      if (typeof window.clarity !== "function") {
        window.clarity = function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      }
      window.clarity("identify", clarityId);
      clarityIdentified = true;
    }
    return {
      hubspotUtk: cookie("hubspotutk") || undefined,
      gaClientId: googleClientId(),
      clarityCustomId: clarityId,
    };
  }

  function ids() {
    let visitorId = cookie(VISITOR_KEY);
    if (!visitorId) {
      visitorId = uuid();
      document.cookie = `${VISITOR_KEY}=${visitorId}; Path=/; Max-Age=31536000; SameSite=Lax; Secure`;
    }
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = uuid();
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return { visitorId, sessionId };
  }

  function privacyState() {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) return { analytics: false, ads: false };
    return {
      analytics: api.analyticsProcessingAllowed() === true,
      ads: api.marketingAllowed() === true && api.saleOfDataAllowed() !== false,
    };
  }

  function acquisition() {
    const params = new URLSearchParams(window.location.search);
    const data = {
      source: params.get("utm_source") || undefined,
      medium: params.get("utm_medium") || undefined,
      campaign: params.get("utm_campaign") || undefined,
      term: params.get("utm_term") || undefined,
    };
    try { data.referrerDomain = document.referrer ? new URL(document.referrer).hostname : undefined; } catch (_) {}
    let clickId;
    for (const type of ["gclid", "fbclid", "msclkid", "ttclid", "li_fat_id"]) {
      if (params.get(type)) { clickId = { type, value: params.get(type) }; break; }
    }
    return { acquisition: data, clickId };
  }

  async function send(eventName, detail) {
    const consent = privacyState();
    if (!consent.analytics) return false;
    const identity = ids();
    const browserSourceIds = await sourceIds(identity.visitorId);
    return fetch(ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        clientEventId: uuid(),
        ...identity,
        eventName,
        eventAt: new Date().toISOString(),
        pagePath: window.location.pathname,
        consent,
        sourceIds: browserSourceIds,
        ...acquisition(),
        ...(detail || {}),
      }),
      keepalive: true,
    }).then((response) => response.ok).catch(() => false);
  }

  function sendIdentity(formId, purpose, email, phone, emailMarketing) {
    if (!allowedPurposes.has(purpose)) return;
    const identity = {
      email: email ? String(email).trim() : undefined,
      phone: phone ? String(phone).trim() : undefined,
    };
    if (!identity.email && !identity.phone) return;
    send("identity_submitted", {
      form: { id: formId, purpose },
      identity,
      consent: { ...privacyState(), emailMarketing: emailMarketing === true },
    });
  }

  function oncePerSession(key) {
    try {
      const name = `snowden_once_${key}`;
      if (sessionStorage.getItem(name)) return false;
      sessionStorage.setItem(name, "1");
      return true;
    } catch (_) { return true; }
  }

  function start() {
    if (started || !privacyState().analytics) return;
    started = true;
    send("visit_started");

    // Email click from Klaviyo: deterministic link to the existing Klaviyo profile.
    if (linkedKlaviyoProfileId) {
      send("email_click_identified", { identity: { klaviyoProfileId: linkedKlaviyoProfileId } });
    }
    // Signed-in Shopify customer: deterministic link to the Shopify customer record.
    const shopifyCustomerId = window.__st && window.__st.cid;
    if (shopifyCustomerId && oncePerSession("customer_authenticated")) {
      send("customer_authenticated", { identity: { shopifyCustomerId: String(shopifyCustomerId) } });
    }

    // Variant chosen on a product page (size / feel). Themes put ?variant=<id> in the address bar when a shopper
    // picks one, and the add-to-cart form's hidden "id" input changes. Only that numeric ID and Shopify's product
    // ID are read; once per variant per session. The variant in the landing URL is already on product_viewed.
    const productMeta = () => window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    const urlVariant = () => { try { return new URL(window.location.href).searchParams.get("variant"); } catch (_) { return null; } };
    let lastVariant = urlVariant();
    function variantSeen(value) {
      const variantId = value ? String(value) : "";
      if (!/\/products\//.test(window.location.pathname) || !/^[0-9]{1,30}$/.test(variantId) || variantId === lastVariant) return;
      lastVariant = variantId;
      if (!oncePerSession(`variant_${variantId}`)) return;
      const meta = productMeta();
      send("variant_selected", { variantId, productId: meta && meta.id ? String(meta.id) : undefined });
    }
    ["pushState", "replaceState"].forEach((name) => {
      const original = window.history[name];
      if (typeof original !== "function") return;
      window.history[name] = function () { const result = original.apply(this, arguments); setTimeout(() => variantSeen(urlVariant()), 0); return result; };
    });
    window.addEventListener("popstate", () => variantSeen(urlVariant()));
    document.addEventListener("change", (event) => {
      const field = event.target;
      if (field && field.name === "id" && field.closest && field.closest('form[action*="/cart/add"]')) variantSeen(field.value);
    }, true);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") visibleStartedAt = Date.now();
      else if (visibleStartedAt) { engagedMs += Date.now() - visibleStartedAt; visibleStartedAt = null; }
    });

    window.addEventListener("scroll", () => {
      const total = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      maxScroll = Math.max(maxScroll, Math.min(100, Math.round((window.scrollY / total) * 100)));
    }, { passive: true });

    document.addEventListener("click", (event) => {
      const element = event.target && event.target.closest && event.target.closest("[data-snowden-region][data-snowden-label]");
      if (!element) return;
      const region = element.getAttribute("data-snowden-region");
      const label = element.getAttribute("data-snowden-label");
      if (!allowedRegions.has(region) || !label || label.length > 200) return;
      send("ui_interacted", { interaction: { region, label } });
    });

    // Financing entry points (intent only; the application itself happens off-site).
    document.addEventListener("click", (event) => {
      if (!event.target || !event.target.closest) return;
      for (const [provider, selector] of FINANCING_PROVIDERS) {
        if (event.target.closest(selector)) {
          if (oncePerSession(`financing_${provider}`)) {
            send("financing_started", { interaction: { region: "financing", label: provider } });
          }
          return;
        }
      }
    }, true);

    // Reviewed capture points (quiz results email).
    document.addEventListener("click", (event) => {
      if (!event.target || !event.target.closest) return;
      for (const capture of REVIEWED_CAPTURES) {
        const button = event.target.closest(capture.submit);
        if (!button) continue;
        const container = button.closest(capture.container);
        const field = container && container.querySelector(capture.email);
        if (field && field.value && field.checkValidity && field.checkValidity()) {
          send("lead_form_submitted", { form: { id: capture.id, purpose: capture.purpose } });
          sendIdentity(capture.id, capture.purpose, field.value, null, capture.emailMarketing);
        }
        return;
      }
    }, true);

    document.addEventListener("focusin", (event) => {
      const form = event.target && event.target.closest && event.target.closest("form[data-snowden-form-id][data-snowden-purpose]");
      if (!form || form.dataset.snowdenStarted === "true") return;
      if (!allowedPurposes.has(form.dataset.snowdenPurpose)) return;
      form.dataset.snowdenStarted = "true";
      send("lead_form_started", { form: { id: form.dataset.snowdenFormId, purpose: form.dataset.snowdenPurpose } });
    });

    document.addEventListener("submit", (event) => {
      const form = event.target && event.target.closest && event.target.closest("form[data-snowden-form-id][data-snowden-purpose]");
      if (!form || !allowedPurposes.has(form.dataset.snowdenPurpose)) return;
      const formDetail = { form: { id: form.dataset.snowdenFormId, purpose: form.dataset.snowdenPurpose } };
      send("lead_form_submitted", formDetail);
      if (form.dataset.snowdenIdentity !== "true") return;
      const emailField = form.querySelector('[data-snowden-identity-field="email"]');
      const phoneField = form.querySelector('[data-snowden-identity-field="phone"]');
      const emailConsentField = form.querySelector('[data-snowden-email-consent="true"]');
      const identity = {
        email: emailField && emailField.value ? emailField.value : undefined,
        phone: phoneField && phoneField.value ? phoneField.value : undefined,
      };
      if (!identity.email && !identity.phone) return;
      send("identity_submitted", {
        ...formDetail,
        identity,
        consent: { ...privacyState(), emailMarketing: emailConsentField ? emailConsentField.checked === true : false },
      });
    });

    // Shopify-native contact and newsletter forms.
    document.addEventListener("submit", (event) => {
      const form = event.target && event.target.closest && event.target.closest(SHOPIFY_FORM_SELECTOR);
      if (!form) return;
      const tags = form.querySelector('input[name="contact[tags]"]');
      const newsletter = !!(tags && /newsletter/i.test(tags.value || ""));
      const purpose = newsletter ? "newsletter" : "contact";
      const formId = newsletter ? "shopify_newsletter" : "shopify_contact";
      const email = form.querySelector('input[name="contact[email]"]');
      const phone = form.querySelector('input[name="contact[phone]"]');
      send("lead_form_submitted", { form: { id: formId, purpose } });
      sendIdentity(formId, purpose, email && email.value, phone && phone.value, newsletter);
    }, true);

    // Klaviyo sign-up forms (pop-ups, flyouts, embeds): the submit event carries the
    // fields the shopper typed into that form.
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event && event.detail;
      if (!detail || detail.type !== "submit") return;
      const meta = detail.metaData || {};
      const formId = `klaviyo_${String(detail.formId || "form").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40)}`;
      sendIdentity(formId, "newsletter", meta.$email, meta.$phone_number, true);
    });

    // HubSpot forms: new (v4) form success event, then the legacy callback message.
    window.addEventListener("hs-form-event:on-submission:success", async (event) => {
      try {
        const api = window.HubSpotFormsV4;
        const form = api && api.getFormFromEvent && api.getFormFromEvent(event);
        const values = form && form.getFormFieldValues ? await form.getFormFieldValues() : [];
        const pick = (suffix) => (values.find((field) => String(field.name || "").endsWith(suffix)) || {}).value;
        sendIdentity("hubspot_form", "contact", pick("/email"), pick("/phone"), false);
      } catch (_) {}
    });
    window.addEventListener("message", (event) => {
      const data = event && event.data;
      if (event.origin !== window.location.origin || !data || data.type !== "hsFormCallback") return;
      if (data.eventName !== "onFormSubmitted" || !data.data) return;
      const values = data.data.submissionValues || {};
      sendIdentity("hubspot_form", "contact", values.email, values.phone, false);
    });

    // HubSpot chat: a started conversation is intent; chat identity stays in HubSpot.
    const hookChat = () => {
      const chat = window.HubSpotConversations;
      if (!chat || !chat.on) return;
      chat.on("conversationStarted", () => send("chat_started", { interaction: { region: "other", label: "hubspot_chat" } }));
    };
    if (window.HubSpotConversations) hookChat();
    else (window.hsConversationsOnReady = window.hsConversationsOnReady || []).push(hookChat);

    window.addEventListener("pagehide", () => {
      if (visibleStartedAt) engagedMs += Date.now() - visibleStartedAt;
      send("content_engaged", { engagement: { engagedSeconds: Math.round(engagedMs / 1000), scrollPercent: maxScroll } });
      send("visit_ended", { engagement: { engagedSeconds: Math.round(engagedMs / 1000), scrollPercent: maxScroll } });
    });
  }

  function loadPrivacy() {
    if (!window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (!error) start();
    });
  }

  document.addEventListener("visitorConsentCollected", start);
  loadPrivacy();
}());
